import type { DerivedSnapshot } from "@/lib/derivedIntel/derivedIntelligenceEngine";

/**
 * Story Arc Engine — maps the current snapshot to a cinematic narrative
 * phase. Pure deterministic, no AI.
 */

export type StoryPhase =
  | "caution"
  | "tension"
  | "dominance"
  | "resistance"
  | "collapse"
  | "reaction"
  | "comeback"
  | "desperation";

export interface StoryArcFrame {
  phase: StoryPhase;
  label: string;
  intensity: number; // 0..1
  detail: string;
}

const PHASE_LABEL: Record<StoryPhase, string> = {
  caution: "Caution",
  tension: "Tension",
  dominance: "Dominance",
  resistance: "Resistance",
  collapse: "Collapse",
  reaction: "Reaction",
  comeback: "Comeback",
  desperation: "Desperation",
};

export interface StoryArcContext {
  scoreHome: number;
  scoreAway: number;
  /** signed momentum trend across the match, optional */
  matchMomentumTrend?: number;
}

export function deriveStoryArc(
  snap: DerivedSnapshot,
  ctx: StoryArcContext,
): StoryArcFrame {
  const s = snap.signals;
  const minute = snap.minute;
  const diff = ctx.scoreHome - ctx.scoreAway;
  const intensity = s.narrativeIntensity;

  // Late game + losing side pushing → desperation
  if (minute >= 80 && diff !== 0) {
    const losingPushing =
      (diff < 0 && s.goalProbabilityHome > 0.5) ||
      (diff > 0 && s.goalProbabilityAway > 0.5);
    if (losingPushing) {
      return phase("desperation", Math.max(intensity, 0.8), "Time running out, losing side pushing all in");
    }
  }
  // Big momentum reversal vs scoreboard → comeback
  if (Math.abs(s.momentumShift) > 0.5 && diff !== 0) {
    const towardsLosing =
      (diff < 0 && s.momentumShift > 0) || (diff > 0 && s.momentumShift < 0);
    if (towardsLosing) return phase("comeback", intensity, "Losing side reclaiming momentum");
  }
  // Collapse — high defensive risk + sustained pressure against
  if (Math.max(s.defensiveRiskHome, s.defensiveRiskAway) > 0.8 && s.pressureExtreme) {
    return phase("collapse", intensity, "Defensive block breaking under pressure");
  }
  // Resistance — extreme pressure but no goal threat materialising
  if (s.pressureExtreme && Math.max(s.goalProbabilityHome, s.goalProbabilityAway) < 0.55) {
    return phase("resistance", intensity, "Holding the line under heavy pressure");
  }
  // Reaction — moderate momentum shift after a flat window
  if (Math.abs(s.momentumShift) > 0.3 && Math.abs(s.momentumShift) <= 0.5) {
    return phase("reaction", intensity, "Match reacts after a flat window");
  }
  // Dominance — sustained territorial dominance
  if (Math.abs(s.territorialDominance) > 0.45 && s.dominantSide !== "balanced") {
    return phase("dominance", intensity, "Sustained territorial dominance");
  }
  // Tension — heating up but no clear side
  if (intensity > 0.4) return phase("tension", intensity, "Pressure rising, balance fragile");
  // Default early/balanced → caution
  return phase("caution", Math.max(0.15, intensity), "Both sides probing, low risk");
}

function phase(p: StoryPhase, intensity: number, detail: string): StoryArcFrame {
  return { phase: p, label: PHASE_LABEL[p], intensity, detail };
}

/**
 * Micro-transition between two consecutive story-arc frames. Returns a
 * short cinematic cue describing how the narrative is evolving. Pure.
 */
export interface StoryMicroTransition {
  from: StoryPhase;
  to: StoryPhase;
  cue: string;
  intensity: number;
}

export function deriveMicroTransition(
  prev: StoryArcFrame | undefined,
  next: StoryArcFrame,
): StoryMicroTransition | null {
  if (!prev || prev.phase === next.phase) return null;
  const intensity = Math.max(prev.intensity, next.intensity);
  const cue = `${PHASE_LABEL[prev.phase]} → ${PHASE_LABEL[next.phase]}`;
  return { from: prev.phase, to: next.phase, cue, intensity };
}